import React, { useState } from 'react';
import {
  Paper,
  Box,
  Typography,
  IconButton,
  Button,
  TextField,
  Switch,
  FormControlLabel,
  Alert,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useTranslation } from 'react-i18next';
import { StringKeys } from '@/resources/strings/StringKeys';

import type {
  PvdConfigurationOnAzure,
  AddPvdConfigRequest,
  UpdatePvdConfigRequest,
} from '@/../generated-client/models';
import { usePvdConfigCreate } from '@/hooks/pvd_config_hooks/usePvdConfigCreate';
import { usePvdConfigEdit } from '@/hooks/pvd_config_hooks/usePvdConfigEdit';

import { VoltageSensitivitySection } from './components/VoltageSensitivitySection';
import CurrentSensitivitySection from './components/CurrentSensitivitySection';
import RadioOptionBox from '@/components/form_elements/RadioOptionBox';
import BaseModal from '@/components/modals/BaseModal';
import FullViewportModalShell from '../../shared/components/FullViewportModalShell';
import CurrentDistanceVisualizer from '../../visualizers/CurrentDistanceVisualizer';
import VoltageDistanceVisualizer from '../../visualizers/VoltageDistanceVisualizer';

import { modalStyles } from '../../modals/unified_config_modals/styles/unifiedConfigStyles';

interface CreateModeProps {
  show: boolean;
  onHide: () => void;
  config: AddPvdConfigRequest;
  onSave: (updated: AddPvdConfigRequest) => void;
  mode: 'create';
  locked?: boolean;
}

interface EditModeProps {
  show: boolean;
  onHide: () => void;
  config: PvdConfigurationOnAzure;
  onSave: (updated: UpdatePvdConfigRequest) => void;
  mode: 'edit';
  locked?: boolean;
}

type Props = CreateModeProps | EditModeProps;

export const PvdConfigModalBase: React.FC<Props> = ({
  show,
  onHide,
  config,
  onSave,
  mode,
  locked,
}) => {
  const { t } = useTranslation();

  const createEditor = usePvdConfigCreate(config as AddPvdConfigRequest);
  const editEditor = usePvdConfigEdit(config as PvdConfigurationOnAzure);
  const editor = mode === 'create' ? createEditor : editEditor;

  // Derived from editor state each render — no intermediate state needed.
  const voltageEnabled = editor.isVoltageEnabled();
  const currentEnabled = editor.isCurrentEnabled();

  const [nameError, setNameError] = useState(false);

  const [visualizersVisible, setVisualizersVisible] = useState(true);

  const [hoveredVoltageSensitivity, setHoveredVoltageSensitivity] = useState<
    number | undefined
  >(undefined);
  const [hoveredCurrentSensitivity, setHoveredCurrentSensitivity] = useState<
    number | undefined
  >(undefined);

  const [shouldForceApply, setShouldForceApply] = useState(false);
  const [showForceApplyDialog, setShowForceApplyDialog] = useState(false);

  const createLocked = mode === 'create' && Boolean(locked);

  const handleSave = () => {
    if (createLocked) return;
    if (mode === 'create' && (!editor.specName || editor.specName.trim() === '')) {
      setNameError(true);
      return;
    }
    if (mode === 'create') {
      const output = createEditor.submitConfig();
      (onSave as (u: AddPvdConfigRequest) => void)(output);
    } else {
      setShouldForceApply(false);
      setShowForceApplyDialog(true);
    }
  };

  const handleForceApplyConfirm = () => {
    const output = editEditor.submitConfig(shouldForceApply);
    (onSave as (u: UpdatePvdConfigRequest) => void)(output);
    setShowForceApplyDialog(false);
    setShouldForceApply(false);
  };

  const handleForceApplyCancel = () => {
    setShowForceApplyDialog(false);
    setShouldForceApply(false);
  };

  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    editor.setSpecName(e.target.value);
    if (nameError && e.target.value.trim() !== '') {
      setNameError(false);
    }
  };

  const modalTitle =
    mode === 'create'
      ? t(StringKeys.CREATE_CONFIGURATION)
      : t(StringKeys.EDIT_CONFIGURATION, { configuration: `${editor.specName}` });

  const submitButtonText = mode === 'create' ? t(StringKeys.CREATE) : t(StringKeys.SAVE);

  return (
    <>
      <FullViewportModalShell open={show} onClose={onHide}>
        <Paper
          sx={{
            width: visualizersVisible
              ? { xs: 'auto', md: '70%', lg: '95vw' }
              : { xs: 'auto', md: '70%', lg: 'auto' },
            maxWidth: visualizersVisible ? { lg: 1400 } : { lg: 800 },
            height: '90vh',
            borderRadius: 3,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
          }}
        >
          <Box sx={modalStyles.header}>
            <Typography variant="h6" fontWeight={600}>
              {modalTitle}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={visualizersVisible}
                    onChange={(e) => setVisualizersVisible(e.target.checked)}
                    sx={{
                      '& .MuiSwitch-switchBase.Mui-checked': {
                        color: '#fff',
                      },
                      '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track':
                        {
                          backgroundColor: '#fff',
                          opacity: 0.5,
                        },
                    }}
                  />
                }
                label={
                  <Typography variant="body2">
                    {t(StringKeys.VISUALIZERS)}
                  </Typography>
                }
                sx={{
                  color: '#fff',
                  m: 0,
                  display: { xs: 'none', md: 'flex' },
                }}
              />
              <IconButton onClick={onHide} sx={{ color: '#fff' }}>
                <CloseIcon />
              </IconButton>
            </Box>
          </Box>

          <Box sx={{ flex: 1, overflowY: 'auto', overflowX: 'hidden' }}>
            {createLocked && (
              <Box sx={{ px: 3, pt: 2, mb: 2 }}>
                <Alert severity="warning">{t(StringKeys.CONFIG_REQUIRES_PAID_PLAN)}</Alert>
              </Box>
            )}
            {mode === 'create' && (
              <Box sx={modalStyles.nameInputSection}>
                <TextField
                  fullWidth
                  label={t(StringKeys.CONFIGURATION_NAME)}
                  placeholder={t(StringKeys.PLEASE_ENTER_A_CONFIGURATION_NAME)}
                  value={editor.specName}
                  onChange={handleNameChange}
                  error={nameError}
                  helperText={
                    nameError
                      ? t(StringKeys.CONFIGURATION_NAME_IS_REQUIRED)
                      : ''
                  }
                  variant="outlined"
                />
              </Box>
            )}

            <Box
              sx={{
                display: 'flex',
                flexDirection: { xs: 'column', lg: 'row' },
                mb: 2,
              }}
            >
              <Box
                sx={{
                  flex: visualizersVisible ? 1 : 'auto',
                  minWidth: 0,
                  width: visualizersVisible ? 'auto' : '100%',
                  position: 'relative',
                  '&::after': {
                    content: '""',
                    position: 'absolute',
                    inset: 0,
                    background: 'linear-gradient(to right, rgba(243, 108, 38, 0.06), transparent 100%)',
                    pointerEvents: 'none',
                    zIndex: 1,
                    '@media (min-width: 900px) and (max-width: 1199px)': {
                      background: 'linear-gradient(to bottom, rgba(243, 108, 38, 0.06), transparent 100%)',
                    },
                  },
                }}
              >
                <VoltageSensitivitySection
                  editor={editor}
                  voltageEnabled={voltageEnabled}
                  setVoltageEnabled={() => {}}
                  onChipHover={(sensitivity) =>
                    setHoveredVoltageSensitivity(sensitivity)
                  }
                  onChipLeave={() => setHoveredVoltageSensitivity(undefined)}
                />
              </Box>
              {visualizersVisible && (
                <Box
                  sx={{
                    flex: 1,
                    minWidth: 0,
                    display: { xs: 'none', md: 'flex' },
                    flexDirection: 'column',
                  }}
                >
                  <VoltageDistanceVisualizer
                    hoveredSensitivity={hoveredVoltageSensitivity}
                    sx={{
                      flex: 1,
                      maxWidth: 'none',
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'flex-start',
                      pt: 2.5,
                    }}
                  />
                </Box>
              )}
            </Box>

            <Box
              sx={{
                display: 'flex',
                flexDirection: { xs: 'column', lg: 'row' },
                mb: 2,
              }}
            >
              <Box
                sx={{
                  flex: visualizersVisible ? 1 : 'auto',
                  minWidth: 0,
                  width: visualizersVisible ? 'auto' : '100%',
                  position: 'relative',
                  '&::after': {
                    content: '""',
                    position: 'absolute',
                    inset: 0,
                    background: 'linear-gradient(to right, rgba(128, 159, 255, 0.06), transparent 100%)',
                    pointerEvents: 'none',
                    zIndex: 1,
                    '@media (min-width: 900px) and (max-width: 1199px)': {
                      background: 'linear-gradient(to bottom, rgba(128, 159, 255, 0.06), transparent 100%)',
                    },
                  },
                }}
              >
                <CurrentSensitivitySection
                  editor={editor}
                  currentEnabled={currentEnabled}
                  setCurrentEnabled={() => {}}
                  onChipHover={(sensitivity) =>
                    setHoveredCurrentSensitivity(sensitivity)
                  }
                  onChipLeave={() => setHoveredCurrentSensitivity(undefined)}
                />
              </Box>
              {visualizersVisible && (
                <Box
                  sx={{
                    flex: 1,
                    minWidth: 0,
                    display: { xs: 'none', md: 'flex' },
                    flexDirection: 'column',
                  }}
                >
                  <CurrentDistanceVisualizer
                    hoveredSensitivity={hoveredCurrentSensitivity}
                    sx={{
                      flex: 1,
                      maxWidth: 'none',
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'flex-start',
                      pt: 2.5,
                    }}
                  />
                </Box>
              )}
            </Box>
          </Box>

          <Box sx={modalStyles.footer}>
            <Button onClick={onHide} sx={modalStyles.cancelButton}>
              {t(StringKeys.CANCEL)}
            </Button>
            <Button
              variant="contained"
              color="secondary"
              onClick={handleSave}
              disabled={createLocked}
            >
              {submitButtonText}
            </Button>
          </Box>
        </Paper>
      </FullViewportModalShell>

      <BaseModal
        open={showForceApplyDialog}
        onClose={handleForceApplyCancel}
        title={modalTitle}
        onSubmit={handleForceApplyConfirm}
        submitButtonText="Apply"
        size="sm"
        sx={{ zIndex: 2100 }}
      >
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {t('choose_how_configuration_change_should_be_applied')}
        </Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          <RadioOptionBox
            selected={!shouldForceApply}
            onClick={() => setShouldForceApply(false)}
            label={t('require_worker_confirmation')}
            description={t('require_worker_confirmation_description')}
          />
          <RadioOptionBox
            selected={shouldForceApply}
            onClick={() => setShouldForceApply(true)}
            label={t('apply_without_worker_confirmation')}
            description={t('apply_without_worker_confirmation_description')}
          />
        </Box>
      </BaseModal>
    </>
  );
};
